/**
 * Who-sent-this context for in-app reports: a short, readable device line
 * ("iPhone · iOS 18.7 · inside Facebook"), the raw user agent and the app
 * version. Stored in `feedback.meta` and shown in the report email.
 *
 * Added 2026-10-08: two "no sound" reports arrived after the iPhone
 * silent-switch fix, and nothing in them said which phone, browser or app
 * version the learner was on.
 *
 * Operator-facing only (DB + report email), so the labels are English and
 * not routed through i18n.
 */

export interface DeviceInfo {
  /** "iPhone · iOS 18.7 · Safari", "Android 14 · inside Facebook", … */
  device: string;
  user_agent: string;
  /** Short git sha of the running build; 'dev' locally. */
  app_version: string;
  /** Opened from the home-screen icon (installed web app). */
  standalone: boolean;
}

export function describeUserAgent(ua: string, touchPoints = 0, standalone = false): string {
  const parts = [describeOs(ua, touchPoints), describeBrowser(ua)];
  if (standalone) parts.push('home-screen app');
  return parts.join(' · ');
}

function describeOs(ua: string, touchPoints: number): string {
  const ios = ua.match(/(iPhone|iPad|iPod)[^)]*? OS (\d+)[_.](\d+)/);
  if (ios) return `${ios[1]} · iOS ${ios[2]}.${ios[3]}`;
  // iPads since iPadOS 13 send a Mac user agent; touch gives them away.
  if (/Macintosh/.test(ua) && touchPoints > 1) return 'iPad';
  const android = ua.match(/Android (\d+(?:\.\d+)?)/);
  if (android) return `Android ${android[1]}`;
  if (/Macintosh/.test(ua)) return 'Mac';
  if (/Windows/.test(ua)) return 'Windows';
  if (/Linux/.test(ua)) return 'Linux';
  return 'unknown device';
}

/** In-app browsers first: their user agents also contain "Safari"/"Chrome". */
function describeBrowser(ua: string): string {
  if (/FBAN\/Messenger|MessengerForiOS|Orca-Android/.test(ua)) return 'inside Messenger';
  if (/FBAN|FBAV|FB_IAB|FB4A/.test(ua)) return 'inside Facebook';
  if (/Instagram/.test(ua)) return 'inside Instagram';
  if (/musical_ly|BytedanceWebview|TikTok/i.test(ua)) return 'inside TikTok';
  if (/ GSA\//.test(ua)) return 'inside Google app';
  if (/CriOS/.test(ua)) return 'Chrome';
  if (/FxiOS|Firefox\//.test(ua)) return 'Firefox';
  if (/EdgiOS|EdgA\/|Edg\//.test(ua)) return 'Edge';
  if (/SamsungBrowser/.test(ua)) return 'Samsung Internet';
  if (/; wv\)/.test(ua)) return 'Android WebView';
  if (/Chrome\//.test(ua)) return 'Chrome';
  if (/Safari\//.test(ua)) return 'Safari';
  return 'unknown browser';
}

function isStandalone(): boolean {
  if (typeof window === 'undefined') return false;
  const nav = navigator as Navigator & { standalone?: boolean };
  if (nav.standalone === true) return true;
  try {
    return window.matchMedia?.('(display-mode: standalone)').matches ?? false;
  } catch {
    return false;
  }
}

export function deviceInfo(): DeviceInfo {
  const ua = typeof navigator === 'undefined' ? '' : navigator.userAgent;
  const touch = typeof navigator === 'undefined' ? 0 : navigator.maxTouchPoints ?? 0;
  const standalone = isStandalone();
  const sha = (import.meta.env.VITE_GIT_SHA as string | undefined) ?? '';
  return {
    device: describeUserAgent(ua, touch, standalone),
    user_agent: ua,
    app_version: sha ? sha.slice(0, 7) : 'dev',
    standalone,
  };
}
